import { createGlobalStyle, css } from "styled-components";
import { cssVariables, PHONE_SIZES } from "./tokens";

// 🎨 Styles communs du nouveau design : jetons (clair, et sombre quand le site est en thème
// sombre, classe « dark-mode » posée par App.js), boutons, badges et signature scientifique.
// Ajoutés à la page par NewDesign.js (seul design du site depuis le tour 65).
// 🔤 Polices de repli à la largeur de Poppins (tour 38) : tant que Poppins charge, les titres gardent
// leurs retours à la ligne. Avant, le titre de l'accueil passait de 2 à 3 lignes à l'arrivée de la
// police et décalait toute la page (CLS 0,53 sur téléphone). Largeurs mesurées : Poppins = 112,5 %
// d'Arial et de Roboto (graisses 400 et 500), 105,7 % d'Arial gras (600). Hauteurs de Poppins
// (ascendante 1,05, descendante 0,35, interligne 0,1), divisées par la même échelle. Arial :
// ordinateurs et iPhone ; Roboto : Android ; sinon la police du système.
export const FALLBACK_FONTS = css`
  @font-face {
    font-family: "Poppins repli";
    src: local("Arial"), local("ArialMT");
    font-weight: 100 599;
    size-adjust: 112.5%;
    ascent-override: 93.3%;
    descent-override: 31.1%;
    line-gap-override: 8.9%;
  }
  @font-face {
    font-family: "Poppins repli";
    src: local("Arial Bold"), local("Arial-BoldMT");
    font-weight: 600 900;
    size-adjust: 105.7%;
    ascent-override: 99.3%;
    descent-override: 33.1%;
    line-gap-override: 9.5%;
  }
  @font-face {
    font-family: "Poppins repli Roboto";
    src: local("Roboto"), local("Roboto-Regular");
    size-adjust: 112.5%;
    ascent-override: 93.3%;
    descent-override: 31.1%;
    line-gap-override: 8.9%;
  }
`;

export const NewDesignStyle = createGlobalStyle`
  ${FALLBACK_FONTS}
  /* 🔔 Notifications (tour 34, Etats.html) : surface, bord gauche de couleur, pastille, ombre douce.
     Classes de notistack. */
  .nd .notistack-MuiContent {
    background: var(--nd-surface) !important;
    color: var(--nd-texte) !important;
    border: 1px solid var(--nd-trait);
    border-left: 4px solid var(--nd-lien);
    border-radius: 14px !important;
    box-shadow: 0 8px 22px var(--nd-toast-ombre) !important;
    font-family: var(--nd-police-texte);
    font-size: 14px !important;
    line-height: 1.45;
  }
  .nd .notistack-MuiContent-success {
    border-left-color: var(--nd-vert-texte);
  }
  .nd .notistack-MuiContent-error {
    border-left-color: var(--nd-danger-texte);
  }
  .nd .notistack-MuiContent-warning {
    border-left-color: var(--nd-orange-texte);
  }
  .nd .notistack-MuiContent button {
    color: var(--nd-texte-doux) !important;
  }
  .nd-toast-icone {
    flex: none;
    width: 22px;
    height: 22px;
    margin-right: 10px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
    background: var(--nd-bleu-doux);
    color: var(--nd-lien-fonce);
  }
  .nd-toast-icone--success {
    background: var(--nd-vert-doux);
    color: var(--nd-vert-texte);
  }
  .nd-toast-icone--error {
    background: var(--nd-rouge-doux);
    color: var(--nd-danger-texte);
  }
  .nd-toast-icone--warning {
    background: var(--nd-orange-doux);
    color: var(--nd-orange-texte);
  }

  .nd {
    ${cssVariables("light")}
  }
  /* ☀️ Pages de l'espace (tour 66, A3) : leur conteneur, à la hauteur de l'écran, prend le fond de la page. Avant,
     sur une page courte (« Aide et support », Favoris…), le fond s'arrêtait avec le contenu : une bande blanche
     jusqu'au pied de page en thème clair (512 px sur « Aide et support »), plus claire en sombre */
  .nd-page {
    background-color: var(--nd-fond-page);
  }
  /* 🌙 Signalement d'un cours (ClassIssueActions, tour 66, A10) : orange et rouge du clair inchangés, ceux du thème
     sombre chaud en sombre (avant : orange et rouge foncés, peu lisibles sur fond sombre) */
  .dark-mode .nd,
  .nd.dark-mode {
    --nd-signal-attente: var(--nd-orange-texte);
    --nd-signal-refus: var(--nd-danger-texte);
  }
  .dark-mode .nd,
  .nd.dark-mode {
    ${cssVariables("dark")}
  }
  @media (max-width: 575.98px) {
    .nd {
      ${Object.entries(PHONE_SIZES).map(([name, value]) => `--nd-taille-${name}: ${value};`).join("\n")}
    }
  }

  /* Flèche de retour en dessin (FlecheRetour.js, tour 60) : à la taille du texte, un peu d'air après.
     Dans un bouton (nd-btn), l'écart du bouton suffit */
  .nd-fleche {
    flex: none;
    width: 1em;
    height: 1em;
    margin-right: 0.35em;
    vertical-align: -0.125em;
  }
  .nd-btn .nd-fleche {
    margin-right: 0;
  }
  /* Flèche « vers la suite » (FlecheSuite.js, tour 61) : l'espace qui la précède est dans le texte */
  .nd-fleche--suite {
    margin-right: 0;
  }

  /* Boutons : principal orange (un seul par écran, texte noir), secondaire bleu, contour.
     44 px de haut au moins, 36 px en petit. */
  .nd-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--nd-espace-2);
    min-height: 44px;
    padding: 0 18px;
    border: 1.5px solid transparent;
    border-radius: var(--nd-rayon-md);
    font-family: var(--nd-police-titre);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }
  .nd-btn:focus-visible {
    outline: 3px solid var(--nd-focus);
    outline-offset: 2px;
  }
  /* Couleur du texte avec les deux classes : plus forte que la règle « .dark-mode a » (texte
     blanc) de l'ancien design, qui blanchissait les boutons faits avec un lien en sombre (tour 39) */
  .nd-btn.nd-btn--principal,
  .nd-btn.nd-btn--principal:hover,
  .nd-btn.nd-btn--principal:focus {
    color: var(--nd-action-texte);
  }
  .nd-btn--principal {
    background: var(--nd-action);
  }
  .nd-btn--principal:hover {
    background: var(--nd-action-survol);
  }
  .nd-btn.nd-btn--secondaire,
  .nd-btn.nd-btn--secondaire:hover,
  .nd-btn.nd-btn--secondaire:focus {
    color: #ffffff;
  }
  .nd-btn--secondaire {
    background: var(--nd-secondaire);
  }
  .nd-btn--secondaire:hover {
    background: var(--nd-secondaire-survol);
  }
  .nd-btn.nd-btn--contour,
  .nd-btn.nd-btn--contour:hover,
  .nd-btn.nd-btn--contour:focus {
    color: var(--nd-lien);
  }
  .nd-btn--contour {
    background: transparent;
    border-color: var(--nd-lien);
    font-family: var(--nd-police-texte);
    font-weight: 500;
  }
  .nd-btn--contour:hover {
    background: var(--nd-bleu-doux);
  }
  .nd-btn--petit {
    min-height: 36px;
    padding: 0 12px;
    font-size: var(--nd-taille-texte-petit);
  }
  .nd-btn:disabled,
  .nd-btn[aria-disabled="true"] {
    cursor: not-allowed;
  }
  .nd-btn--principal:disabled,
  .nd-btn--principal[aria-disabled="true"] {
    background: var(--nd-action-desactive);
  }
  .nd-btn--secondaire:disabled,
  .nd-btn--contour:disabled {
    opacity: 0.55;
  }

  /* Badges : pilule, fond doux et texte de la même couleur */
  .nd-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: var(--nd-rayon-pilule);
    font-family: var(--nd-police-texte);
    font-size: var(--nd-taille-etiquette);
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
  }
  .nd-badge--bleu {
    background: var(--nd-bleu-doux);
    color: var(--nd-lien-fonce);
  }
  .nd-badge--orange {
    background: var(--nd-orange-doux);
    color: var(--nd-orange-texte);
  }
  .nd-badge--vert {
    background: var(--nd-vert-doux);
    color: var(--nd-vert-texte);
  }
  .nd-point {
    width: 8px;
    height: 8px;
    flex: none;
    border-radius: 50%;
    background: currentColor;
  }
  .nd-point--en-ligne {
    background: var(--nd-en-ligne);
  }

  /* Signature scientifique : papier millimétré (28 px) et courbe qui se trace lentement, une
     fois. Arrêtée chez les visiteurs qui ont réglé « réduire les animations ». */
  .nd-signature {
    position: relative;
    overflow: hidden;
    background-color: var(--nd-fond);
    background-image: linear-gradient(var(--nd-quadrillage) 1px, transparent 1px),
      linear-gradient(90deg, var(--nd-quadrillage) 1px, transparent 1px);
    background-size: 28px 28px;
  }
  .nd-signature__courbes {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    -webkit-mask-image: linear-gradient(90deg, transparent 0 42%, #000 62%);
    mask-image: linear-gradient(90deg, transparent 0 42%, #000 62%);
  }
  .nd-signature__formule {
    position: absolute;
    top: 22px;
    right: 32px;
    font-family: var(--nd-police-formule);
    font-size: var(--nd-taille-formule);
    color: var(--nd-formule);
    pointer-events: none;
  }
  .nd-signature__contenu {
    position: relative;
  }
  .nd-trace {
    stroke-dasharray: 4000;
    stroke-dashoffset: 0;
  }
  .nd-signature--anim .nd-trace {
    stroke-dashoffset: 4000;
    animation: nd-trace 8s ease-out 0.15s forwards;
  }
  @keyframes nd-trace {
    to {
      stroke-dashoffset: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .nd-signature--anim .nd-trace {
      animation: none;
      stroke-dashoffset: 0;
    }
  }
  /* ℹ️ Icône « i » et son explication (tour 43, InfoNd.js) : zone de clic de 28 px, texte dans la
     page sous la phrase, contour au focus */
  .nd-info {
    display: inline;
  }
  .nd-info__bouton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin: -6px 0 -6px 2px;
    padding: 0;
    vertical-align: middle;
    border: 0;
    border-radius: 50%;
    background: none;
    color: var(--nd-lien);
    cursor: pointer;
  }
  .nd-info__bouton:hover,
  .nd-info__bouton[aria-expanded="true"] {
    background: var(--nd-bleu-doux);
  }
  .nd-info__bouton:focus-visible {
    outline: 3px solid var(--nd-focus);
    outline-offset: 1px;
  }
  .nd-info__texte {
    display: block;
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: var(--nd-rayon-md);
    background: var(--nd-panneau);
    border: 1px solid var(--nd-trait);
    color: var(--nd-texte);
    font-size: 14px;
    line-height: 1.5;
  }
  .nd-info__texte[hidden] {
    display: none;
  }
  /* 📶 Escalier des niveaux (tour 43, Escalier.js) : trait et couleur de la courbe, marches
     atteintes en bleu foncé (bleu clair en sombre), point sur la marche du niveau. Courte
     transition, coupée si « réduire les animations ». */
  .nd-escalier {
    display: block;
    overflow: visible;
    pointer-events: none;
  }
  .nd-escalier__axes {
    fill: none;
    stroke: var(--nd-axe);
    stroke-width: 1.5;
    vector-effect: non-scaling-stroke;
  }
  .nd-escalier__marche {
    fill: none;
    stroke: var(--nd-courbe);
    stroke-width: var(--nd-escalier-trait, 4);
    stroke-linecap: round;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;
  }
  .nd-escalier__marche--atteinte {
    stroke: var(--nd-lien);
  }
  .nd-escalier__point {
    fill: var(--nd-lien);
    stroke: var(--nd-fond);
    stroke-width: 2;
    vector-effect: non-scaling-stroke;
    opacity: 0;
  }
  .nd-escalier__point--visible {
    opacity: 1;
  }
  .nd-escalier--anim .nd-escalier__marche {
    transition: stroke 0.3s ease;
  }
  .nd-escalier--anim .nd-escalier__point {
    transition: transform 0.3s ease, opacity 0.3s ease;
  }
  @media (prefers-reduced-motion: reduce) {
    .nd-escalier--anim .nd-escalier__marche,
    .nd-escalier--anim .nd-escalier__point {
      transition: none;
    }
  }
  /* 🌙 Fenêtres en thème sombre (tour 37) : public/index.html force le blanc dans .modal-content
     (body.rs-theme-dark .modal-content * { color: #fff !important; }). Le bouton orange garde
     son texte foncé, comme hors des fenêtres (blanc sur orange : contraste trop faible). Pareil
     pour le bouton orange du planning (tour 49). */
  body.nd.rs-theme-dark .modal-content .nd-btn--principal,
  body.nd.rs-theme-dark .modal-content .acd-btn-primary {
    color: var(--nd-action-texte) !important;
  }
  /* 🌙 Gris chauds en sombre (tour 49) : public/index.html et global.js forcent des gris neutres
     (fond de la page, fenêtres, champs et zones de texte). Dans le nouveau design, ces éléments
     prennent les jetons. L'ancien design garde ses valeurs. */
  body.nd.dark-mode {
    background-color: var(--nd-fond) !important;
  }
  body.nd.rs-theme-dark .modal-content {
    background-color: var(--nd-surface) !important;
  }
  /* Texte secondaire de Bootstrap (text-body-secondary) : gris foncé du thème clair, illisible sur les
     fonds sombres (1,12:1 sur la fiche). Texte secondaire du site, en sombre seulement (tour 50) */
  body.nd.dark-mode .text-body-secondary {
    color: var(--nd-texte-doux) !important;
  }
  body.nd.dark-mode .input-field {
    background-color: var(--nd-champ) !important;
    border-color: var(--nd-champ-bord) !important;
    color: var(--nd-texte) !important;
  }
  /* Champs libres de « Autre établissement » et « Autre » (domaine d'études) : couleurs des champs du site
     en sombre, au lieu du champ blanc de Bootstrap (tour 54) */
  body.nd.dark-mode .nd-champ-libre {
    background-color: var(--nd-champ) !important;
    border-color: var(--nd-champ-bord) !important;
    color: var(--nd-texte) !important;
  }
  body.nd.dark-mode .nd-champ-libre::placeholder {
    color: var(--nd-texte-doux);
  }
  body.nd.dark-mode textarea {
    background-color: var(--nd-champ) !important;
    color: var(--nd-texte) !important;
  }
  /* 🌙 Encarts clairs de Bootstrap dans les fenêtres (tour 65, A10) : public/index.html force le texte en
     blanc dans .modal-content, l'encart restait blanc. Texte invisible dans « Prêt pour votre cours ? »
     (case « Ne plus afficher ce message »), « Toutes nos félicitations ! » (durée du cours), « Succès ! »
     du prof (montant) et le bouton « Retour » de la salle. En sombre seulement, le clair ne change pas */
  body.nd.rs-theme-dark .modal-content .bg-light,
  body.nd.rs-theme-dark .modal-content .btn-light {
    background-color: var(--nd-fond-page) !important;
    border-color: var(--nd-trait) !important;
  }
  /* Récapitulatif du prix de la fiche (PriceSummary, bg-body-tertiary) : encart clair sous un texte
     clair, illisible en sombre (tour 65, A10) */
  body.nd.dark-mode .bg-body-tertiary {
    background-color: var(--nd-surface) !important;
  }
  body.nd.dark-mode .bg-body-tertiary.border {
    border-color: var(--nd-trait) !important;
  }
  body.nd.dark-mode .bg-body-tertiary .text-muted {
    color: var(--nd-texte-doux) !important;
  }
`;
